import DataGrid, { ColumnDataGrid } from 'components/DataGrid'
import Icon from 'components/Icon'
import { TopToolbar } from 'components/lists'
import { Pagination } from 'features/pagination'
import {
    InviteOrder,
    InviteStatus,
    InviteType,
    GetStatusOverviewQuery,
    GetStatusOverviewQueryVariables
} from 'generated/graphql'
import { useEventSlug, useQuery } from 'hooks'
import React, {
    useCallback, useEffect, useMemo, useState
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import { colors, _ } from 'utils'
import { useHistory } from 'react-router'
import { omitBy, isEmpty } from 'lodash'
import { S as STooltip } from 'components/InfoTooltip'
import { OverlayTrigger } from 'react-bootstrap'
import routes from 'routes'
import { maxParticipantsSelector } from 'features/events/eventSlice'
import TagsIndicator from 'components/lists/TagsIndicator'
import MoreOptionsButton from '../components/MoreOptionsButton'
import { useInvites } from '../hooks'
import InviteActionsTopToolbar from '../components/InviteActionsTopToolbar'
import { setIsStaff } from '../graphql/mutation'
import { statusOverviewQuery } from '../graphql/query'
import AttendeesOverview from '../components/AttendeesOverview'
import { toLocalDateFormat, toLocalDateTimeFormat } from '../../../utils/datetime'
import { handleSubmit, importProps } from '../importConfig'

const S = {
    Wrapper: styled.div`
        width: 100%;
        padding: 0 10px;
    `,
    DataGrid: styled(DataGrid)`
        width: calc(100% + 40px);
        margin: 0 -20px;
        & .datagrid__row-header, & .datagrid__row-item {
            border-bottom: 1px solid ${colors.gray1};
        }
        & .datagrid__row-item {
            height: 52px;
            font-size: 16px;
            color: ${colors.grayTitle};
        }
        & .datagrid__column-header:first-child {
            padding-left: 20px;
        }
        & .infinite-scroll-component {
            overflow: visible !important;
        }
    `,
    StaffIcon: styled.div`
        width: 100%;
        display: flex;
        justify-content: center;
    `,
    Tag: styled.div<{ tagColor: string }>`
        display: flex;
        align-items: center;
        justify-content: center;
        width: 16px;
        height: 16px;
        margin: auto;
        border-radius: 3px;
        background-color: ${({ tagColor }) => tagColor};
        color: white;
        font-size: 14px;
        font-weight: bold;
    `,
    TagTooltip: styled.div<{ tagColor: string }>`
        position: relative;
        min-width: 100px;
        margin-bottom: 6px;
        padding-left: 18px;
        font-size: 14px;
        font-weight: 300;
        color: ${colors.dark};
        text-align: left;
        &:before {
            content: '';
            position: absolute;
            left: 0;
            top: 3px;
            width: 12px;
            height: 12px;
            border-radius: 3px;
            background-color: ${({ tagColor }) => tagColor};
        }
    `
}

export type StatusOverview = {
    new: number,
    pending: number,
    accepted: number,
    total: number,
}

const Attendees = () => {
    const dispatch = useDispatch()
    const eventSlug = useEventSlug()
    const history = useHistory()
    const [search, setSearch] = useState('')
    const [sort, setSort] = useState<InviteOrder>(null)
    const [status, setStatus] = useState<InviteStatus>(null)
    const [data, setData] = useState<InviteType[]>([])
    const {
        list, requestStatus, pagination, totalItems, handlePageChange, fetchData
    } = useInvites({
        eventSlug,
        ...omitBy({
            search,
            sort,
            status,
        }, isEmpty)
    })

    useEffect(() => {
        if (list) {
            setData(list)
        }
    }, [list])
    const maxParticipants = useSelector(maxParticipantsSelector)

    const newStatusOverview = useQuery<
        GetStatusOverviewQuery, GetStatusOverviewQueryVariables
    >(statusOverviewQuery, { eventSlug, status: InviteStatus.New })
    const pendingStatusOverview = useQuery<
        GetStatusOverviewQuery, GetStatusOverviewQueryVariables
    >(statusOverviewQuery, { eventSlug, status: InviteStatus.Pending })
    const acceptedStatusOverview = useQuery<
        GetStatusOverviewQuery, GetStatusOverviewQueryVariables
    >(statusOverviewQuery, { eventSlug, status: InviteStatus.Accepted })
    const statusOverview: StatusOverview = {
        new: newStatusOverview?.data?.event?.invites?.totalCount || 0,
        pending: pendingStatusOverview?.data?.event?.invites?.totalCount || 0,
        accepted: acceptedStatusOverview?.data?.event?.invites?.totalCount || 0,
        total: 0,
    }
    statusOverview.total = statusOverview.new + statusOverview.pending + statusOverview.accepted

    const fetchAllData = useCallback(() => {
        fetchData()
        newStatusOverview.fetchData()
        pendingStatusOverview.fetchData()
        acceptedStatusOverview.fetchData()
    }, [acceptedStatusOverview, fetchData, newStatusOverview, pendingStatusOverview])

    const MoreOptionsButtonMemo = useMemo(
        () => (invite: InviteType) => <MoreOptionsButton invite={invite} fetchData={fetchAllData} />,
        [fetchAllData]
    )

    const handleSetIsStaff = (event, row) => {
        event.stopPropagation()
        setIsStaff(eventSlug, row.email, !row.isStaff)
        setData(data.map((item) => {
            if (item.id === row.id) {
                return { ...item, isStaff: !row.isStaff }
            }
            return item
        }))
    }

    const columns: ColumnDataGrid[] = useMemo(() => [{
        name: 'tag',
        width: 1,
        header: _('attendee.list.tag_header'),
        render: (row: InviteType) => <TagsIndicator row={row} />
    },
    {
        name: 'name',
        width: 2,
        sortable: true,
        header: _('attendee.list.name_header'),
        render: (row: InviteType) => (
            <span>
                {`${row.firstName || ''} ${row.lastName || ''}`}
            </span>
        )
    },
    {
        name: 'email',
        width: 3,
        sortable: true,
        header: _('attendee.list.email_header'),
        render: (row: InviteType) => (
            <div className="text-cut">
                {row.email}
            </div>
        )
    },
    {
        name: 'status',
        width: 1,
        header: _('attendee.list.status_header'),
        render: (row: InviteType) => (<span className="text-uppercase">{row.status}</span>)
    },
    {
        name: 'created',
        width: 1,
        sortable: true,
        header: _('attendee.list.created_date_header'),
        render: (row: InviteType) => (<span>{toLocalDateFormat(row.created)}</span>)
    },
    {
        name: 'isStaff',
        width: 1,
        header: _('attendee.list.is_staff_header'),
        render: (row: InviteType) => (
            <OverlayTrigger
                placement="bottom"
                overlay={(
                    <STooltip.Tooltip id="info-tooltip">
                        {_('attendee.list.update_staff_tooltip')}
                    </STooltip.Tooltip>
                )}
            >
                <S.StaffIcon onClick={(event) => handleSetIsStaff(event, row)}>
                    <Icon
                        icon="account"
                        color={row.isStaff ? colors.primary : colors.icon}
                        size={20}
                    />
                </S.StaffIcon>
            </OverlayTrigger>
        )
    },
    {
        name: 'mailSent',
        width: 2,
        sortable: true,
        header: _('attendee.list.mail_sent_header'),
        render: (row: InviteType) => (
            <span>
                {row.mailSent && toLocalDateTimeFormat(row.mailSent)}
            </span>
        )
    },
    {
        name: 'actions',
        width: 1,
        render: MoreOptionsButtonMemo,
        style: {
            padding: 0,
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }], [MoreOptionsButtonMemo, eventSlug])

    return (
        <S.Wrapper>
            <TopToolbar
                addItemText={_('attendee.list.add_item_button')}
                onAddItem={() => history.push(routes.createAttendee.getPath({ eventSlug }))}
                disabledAdd={maxParticipants <= statusOverview.total}
                onSearch={setSearch}
                searchPlaceHolder={_('attendee.list.search_placeholder')}
                filters={[
                    {
                        options: [
                            { label: _('attendee.list.new_status'), value: InviteStatus.New },
                            { label: _('attendee.list.pending_status'), value: InviteStatus.Pending },
                            { label: _('attendee.list.accepted_status'), value: InviteStatus.Accepted },
                        ],
                        onChange: setStatus,
                        isClearable: true,
                        placeholder: _('attendee.list.filter_status_placeholder')
                    },
                ]}
                renderExtraActions={() => <InviteActionsTopToolbar statusOverview={statusOverview} />}
                bulkActions={maxParticipants > statusOverview.total && {
                    importProps: {
                        ...importProps,
                        onSubmit: (_data) => handleSubmit(eventSlug, _data, dispatch, fetchData),
                    }
                }}
            />
            <AttendeesOverview
                statusOverview={statusOverview}
                reachedLimit={maxParticipants <= statusOverview.total}
            />
            <S.DataGrid
                data={data}
                columns={columns}
                theme="light"
                hideHeader={false}
                hideAction
                onSort={setSort}
                requestStatus={requestStatus}
                onRowClick={(row) => history.push(routes.updateAttendee.getPath({ eventSlug, id: row.id }))}
            />
            <Pagination
                page={pagination?.page}
                onPageChange={handlePageChange}
                totalItems={totalItems}
                disabled={requestStatus === 'loading'}
                itemPerPage={50}
                itemsPerPageOpts={[50, 100, 150]}
            />
        </S.Wrapper>
    )
}

export default Attendees
